// Open covenant breaches — the loan Compliance tab's lead strip. Renders
// every open breach condition from the loan health feed (the rows
// LoanHealthService marks kind === "breach": DSCR Fail/Watch, open
// LoanFailures, vacant required-vendor slots), severity-tinted, each item
// clicking through to its owning tab. Hidden when there are none — calm
// by default (AttentionStrip renders nothing at zero counts).

import { useMemo } from "react";
import { useLoanHealth } from "@/hooks/portfolioAdmin/useLoanHealth";
import {
  AttentionStrip,
  type AttentionChip,
} from "../common/AttentionStrip";

export function CovenantBreachStrip({ loanId }: { loanId: string }) {
  const health = useLoanHealth(loanId);

  const chips = useMemo<AttentionChip[]>(() => {
    const breaches = (health.data?.attention ?? []).filter(
      (a) => a.kind === "breach",
    );
    const toItem = (a: (typeof breaches)[number]) => ({
      id: a.id,
      title: a.title,
      owner: a.ownerRole,
      href: `/portfolio-admin/loans/${loanId}?tab=${a.targetTab}`,
    });
    const errors = breaches.filter((a) => a.severity === "error");
    const warnings = breaches.filter((a) => a.severity === "warning");
    return [
      {
        id: "breaches",
        label: "Open covenant breaches",
        count: errors.length,
        tone: "error",
        items: errors.map(toItem),
      },
      {
        id: "watch",
        label: "Covenant watch items",
        count: warnings.length,
        tone: "warning",
        items: warnings.map(toItem),
      },
    ];
  }, [health.data, loanId]);

  // A failed health read degrades to nothing — the tab's other sections
  // carry their own error states.
  if (health.isError) return null;

  return <AttentionStrip chips={chips} testId="covenant-breach-strip" />;
}
